import React from 'react';
import {
  Avatar,
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  makeStyles,
  Typography,
  Tooltip,
  Button
} from '@material-ui/core';
import PropTypes from 'prop-types';
import { get, map, find, isEmpty } from 'lodash';
import clsx from 'clsx';
import YouTubeIcon from '@mui/icons-material/YouTube';
import moment from 'moment';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import HelpIcon from '@mui/icons-material/Help';
import numeral from 'numeral';
import ElementColors from '../../../constants/ElementColors';
import AttackAssets from '../../../constants/AttackAssets';
import { useSelector } from 'react-redux';

const useStyles = makeStyles(theme => ({
  '@keyframes pulsate': {
    '0%': {
      color: 'rgba(151,151,151, 0.2)'
    },
    '50%': {
      color: 'rgba(151,151,151, 1)'
    },
    '100%': {
      color: 'rgba(151,151,151, 0.2)'
    }
  },
  pulsating: {
    animation: '$pulsate 3s ease-in-out infinite'
  },
  oddRow: {
    // backgroundColor: theme.palette.background.paper,
    height: '2rem',
    '&:hover': {
      backgroundColor: `${theme.palette.primary.hover}40`
    }
  },
  evenRow: {
    // backgroundColor: theme.palette.background.paper,
    height: '2rem',
    '&:hover': {
      backgroundColor: `${theme.palette.primary.hover}40`
    }
  },
  tableHeader: {
    zIndex: 1
  },
  tableView: {
    borderRadius: '5px',
    overflow: 'auto'
  },
  tHeaderCol: {
    textAlign: 'center',
    // backgroundColor: '#f7f7f9',
    // color: '#a6abbd',
    height: '4rem',
    fontSize: '12px',
    fontWeight: '400',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline'
    }
  },
  tHeaderColStatic: {
    textAlign: 'center',
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.secondary,
    height: '4rem',
    fontSize: '12px',
    fontWeight: '400'
  },
  tCol: {
    textAlign: 'center',
    color: theme.palette.background.dark,
    borderTop: '1px solid #cdd2d21A',
    fontSize: '12px'
  },
  teamDisplay: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center'
  },
  tBody: {
    marginTop: '-3px'
  },
  timeEntry: {
    display: 'flex',
    justifyContent: 'space-evenly'
  },
  banner: {
    width: '100%',
    background: 'none'
  },
  profileLink: {
    // color: theme.palette.background.dark,
    fontWeight: '700',
    '&:hover': {
      color: theme.palette.text.primary
    },
    fontSize: '12px'
  },
  videoLink: {
    color: theme.palette.secondary.main,
    '&:hover': {
      color: theme.palette.tertiary.secondary
    },
    cursor: 'pointer'
  },
  hidden: {
    display: 'none'
  },
  tooltip: {
    fontSize: '12px'
  },
  characterImage: {
    backgroundColor: '#ebeef1',
    boxShadow: '0px 0px 1px 1px #cdd2d2',
    border: '1px solid #cdd2d2',
    height: '35px',
    width: '35px',
    margin: '0.1rem'
  },
  empty: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '1rem',
    marginBottom: '1rem',
    color: theme.palette.text.primary
  },
  details: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  text: {
    fontSize: '12px',
    color: '#8c96a2'
  },
  tableContainer: {
    border: '1px solid #ced3d3',
    boxShadow: '0px 0px 1px 1px #cdd2d2'
  }
}));

function UserProfileDPSTable(props) {
  const classes = useStyles();
  const { characterAssets } = useSelector(state => state.gameAssets);
  const tableHeaderIds = map(props.headers, 'id');

  const detailTooltips = {
    account_snapshot:
      'If character information is recorded for a run, you can view it here'
  };

  return (
    <Box classes={{ root: classes.tableContainer }}>
      <Table className={classes.tableView} stickyHeader size="small">
        <TableHead className={classes.tableHeader}>
          <TableRow>
            {map(props.headers, header => {
              return (
                <TableCell
                  key={header.id}
                  id={header.id}
                  className={classes.tHeaderColStatic}
                >
                  {!!detailTooltips[header.id] && (
                    <Tooltip
                      classes={{ tooltip: classes.tooltip }}
                      title={detailTooltips[header.id]}
                    >
                      <Box classes={{ root: classes.details }}>
                        {header.label}
                        <Box mr={1} />
                        <HelpIcon style={{ color: '#ced3d3' }} />
                      </Box>
                    </Tooltip>
                  )}
                  {!detailTooltips[header.id] && header.label}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody className={classes.tBody}>
          {map(props.rows, (entry, idx) => {
            return (
              <TableRow
                className={clsx({
                  [classes.root]: true,
                  [classes.oddRow]: idx % 2,
                  [classes.evenRow]: !(idx % 2)
                })}
                key={`${entry._id}-${idx}`}
              >
                <TableCell
                  className={clsx({
                    [classes.tCol]: find(
                      tableHeaderIds,
                      h => h === 'dps_category'
                    ),
                    [classes.hidden]: !find(
                      tableHeaderIds,
                      h => h === 'dps_category'
                    )
                  })}
                >
                  <Typography classes={{ root: classes.text }}>
                    {get(entry, 'dps_category')}
                  </Typography>
                </TableCell>
                <TableCell
                  className={clsx({
                    [classes.tCol]: find(
                      tableHeaderIds,
                      h => h === 'damage_dealt'
                    ),
                    [classes.hidden]: !find(
                      tableHeaderIds,
                      h => h === 'damage_dealt'
                    )
                  })}
                >
                  <Typography classes={{ root: classes.text }}>
                    {numeral(get(entry, 'damage_dealt')).format('0,0')}
                  </Typography>
                  <Typography classes={{ root: classes.text }}>
                    {entry.character_ceiling &&
                      get(entry.character_ceiling, get(entry, 'attack_type')) &&
                      '(' +
                        numeral(
                          entry.damage_dealt /
                            get(
                              entry.character_ceiling,
                              get(entry, 'attack_type')
                            )
                        ).format('0.00%') +
                        ')'}
                  </Typography>
                  <Typography classes={{ root: classes.text }}>
                    {!(
                      entry.character_ceiling &&
                      get(entry.character_ceiling, get(entry, 'attack_type'))
                    ) && '— —'}
                  </Typography>
                </TableCell>
                <TableCell
                  className={clsx({
                    [classes.tCol]: find(
                      tableHeaderIds,
                      h => h === 'dps_character'
                    ),
                    [classes.hidden]: !find(
                      tableHeaderIds,
                      h => h === 'dps_character'
                    )
                  })}
                >
                  <Box className={classes.teamDisplay}>
                    <Avatar
                      src={get(
                        characterAssets[get(entry, 'dps_character')],
                        'avatar'
                      )}
                      key={`${idx}-${get(entry, 'dps_character')}`}
                      className={classes.characterImage}
                    />
                  </Box>
                </TableCell>
                <TableCell
                  className={clsx({
                    [classes.tCol]: find(
                      tableHeaderIds,
                      h => h === 'attack_type'
                    ),
                    [classes.hidden]: !find(
                      tableHeaderIds,
                      h => h === 'attack_type'
                    )
                  })}
                >
                  <Box className={classes.teamDisplay}>
                    <Avatar
                      style={{
                        backgroundColor: get(
                          ElementColors,
                          characterAssets[entry.dps_character].element
                        )[0],
                        border: `2px solid ${
                          get(
                            ElementColors,
                            characterAssets[entry.dps_character].element
                          )[1]
                        }`
                      }}
                      src={get(
                        characterAssets[get(entry, 'dps_character')],
                        AttackAssets[get(entry, 'attack_type')]
                      )}
                      key={`${idx}-${get(entry, 'attack_type')}`}
                      classes={{ img: classes.skillImg }}
                    />
                  </Box>
                </TableCell>
                <TableCell
                  className={clsx({
                    [classes.tCol]: find(
                      tableHeaderIds,
                      h => h === 'video_link'
                    ),
                    [classes.hidden]: !find(
                      tableHeaderIds,
                      h => h === 'video_link'
                    )
                  })}
                >
                  <Tooltip title="Watch video">
                    <Box onClick={props.loadVideo(entry)}>
                      <YouTubeIcon classes={{ root: classes.videoLink }} />
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell
                  className={clsx({
                    [classes.tCol]: find(tableHeaderIds, h => h === 'team'),
                    [classes.hidden]: !find(tableHeaderIds, h => h === 'team')
                  })}
                >
                  <Box className={classes.teamDisplay}>
                    {map(entry.team, char => {
                      return (
                        <Avatar
                          src={get(characterAssets[char], 'avatar')}
                          key={`${idx}-${char}`}
                          alt={char}
                          className={classes.characterImage}
                        />
                      );
                    })}
                  </Box>
                </TableCell>
                <TableCell
                  className={clsx({
                    [classes.tCol]: find(
                      tableHeaderIds,
                      h => h === 'created_at'
                    ),
                    [classes.hidden]: !find(
                      tableHeaderIds,
                      h => h === 'created_at'
                    )
                  })}
                >
                  <Typography classes={{ root: classes.text }}>
                    {moment(get(entry, 'created_at')).format('MM/DD/YYYY')}
                  </Typography>
                </TableCell>
                <TableCell
                  className={clsx({
                    [classes.tCol]: find(
                      tableHeaderIds,
                      h => h === 'account_snapshot'
                    ),
                    [classes.hidden]: !find(
                      tableHeaderIds,
                      h => h === 'account_snapshot'
                    )
                  })}
                >
                  {entry.account_snapshot && (
                    <Tooltip
                      title="Character Details Available"
                      classes={{ tooltip: classes.tooltip }}
                    >
                      <Button
                        onClick={props.loadSnapshot(entry.account_snapshot)}
                      >
                        <ZoomInIcon classes={{ root: classes.pulsating }} />
                      </Button>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {isEmpty(props.rows) && (
        <Box classes={{ root: classes.empty }}>
          <Typography variant="h4">No information to display.</Typography>
        </Box>
      )}
    </Box>
  );
}

UserProfileDPSTable.propTypes = {
  headers: PropTypes.array.isRequired,
  rows: PropTypes.array.isRequired,
  sortBy: PropTypes.string.isRequired,
  sortDir: PropTypes.string.isRequired,
  loadVideo: PropTypes.func.isRequired,
  loadSnapshot: PropTypes.func.isRequired
};

export default UserProfileDPSTable;
